// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Remote query hook backed by D1 REST + @tanstack/react-query.
 *
 * Returns the same `{ data, isLoading, error }` shape as local RxDB hooks
 * so consumers can switch between local and remote without API changes.
 */

import { useEffect, useState } from 'react'
import { useQuery as useTanstackQuery } from '@tanstack/react-query'
import { useCollectionMode } from '../CollectionModeContext'
import { collectionKeys } from '../../remote/queryKeys'
import type { D1QueryParams } from '../../remote/d1Client'

export interface UseRemoteQueryOptions {
  selector?: Record<string, unknown>
  sort?: Array<Record<string, 'asc' | 'desc'>>
  limit?: number
  skip?: number
  staleTime?: number
  refetchInterval?: number | false
  enabled?: boolean
}

export interface UseRemoteQueryResult<T> {
  data: T[]
  isLoading: boolean
  error: Error | null
}

export function useRemoteQuery<T = Record<string, unknown>>(
  collectionName: string,
  options: UseRemoteQueryOptions = {},
): UseRemoteQueryResult<T> {
  const ctx = useCollectionMode()
  if (!ctx) throw new Error('useRemoteQuery requires CollectionModeProvider')

  const { client, config } = ctx
  const { selector, sort, limit, skip, enabled = true } = options

  const params: D1QueryParams = { selector: selector ?? {}, sort, limit, skip }

  const result = useTanstackQuery({
    queryKey: collectionKeys.query(collectionName, params),
    queryFn: () => client.query<T>(collectionName, params),
    staleTime: options.staleTime ?? config.remoteDefaults?.staleTime ?? 30_000,
    refetchInterval: options.refetchInterval ?? config.remoteDefaults?.refetchInterval ?? false,
    enabled,
  })

  return {
    data: result.data ?? [],
    isLoading: result.isLoading,
    error: result.error,
  }
}

/**
 * Conditional variant safe to use without a CollectionModeProvider.
 *
 * Uses useState/useEffect directly (no tanstack hooks) so it maintains
 * a consistent hook count regardless of whether a provider is mounted.
 * When the provider IS mounted, uses QueryClient imperatively for caching.
 * When `collectionName` is null or no provider exists, returns empty data.
 */
export function useRemoteQueryConditional<T = Record<string, unknown>>(
  collectionName: string | null,
  options?: UseRemoteQueryOptions,
): UseRemoteQueryResult<T> {
  const ctx = useCollectionMode()
  const isActive = ctx !== null && collectionName !== null && (options?.enabled ?? true)

  const [data, setData] = useState<T[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const selectorKey = JSON.stringify(options?.selector ?? {})
  const sortKey = JSON.stringify(options?.sort ?? [])
  const { limit, skip } = options ?? {}

  useEffect(() => {
    if (!isActive || !ctx || !collectionName) {
      setData([])
      setIsLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    const params: D1QueryParams = {
      selector: options?.selector ?? {},
      sort: options?.sort,
      limit,
      skip,
    }

    ctx.client
      .query<T>(collectionName, params)
      .then((result: T[]) => {
        if (!cancelled) {
          setData(result)
          setIsLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selector/sort serialized as keys
  }, [isActive, ctx, collectionName, selectorKey, sortKey, limit, skip])

  return { data, isLoading, error }
}
